import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./SetPassword.css";

export default function SetPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [timer, setTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const inputRefs = useRef([]);
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const paramEmail = new URLSearchParams(window.location.search).get("email");
    if (paramEmail) setEmail(paramEmail);
  }, []);

  useEffect(() => {
    let interval = null;
    if (timerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive, timer]);

  const requestCode = async () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setMessage("Please enter a valid email address.");
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/users/request-password-reset`, { email });

      if (res.data.exists === false) {
        setMessage("Email not found. Please enter your registered email.");
        return;
      }

      setMessage("A verification code has been sent to your email.");
      setStep(2);
      setTimer(60);
      setTimerActive(true);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setMessage(err.response.data.message);
      } else {
        setMessage("Something went wrong. Try again.");
      }
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const verifyCode = async () => {
    const code = codeDigits.join("");
    if (code.length !== 6) {
      setMessage("Please enter the complete 6-digit code.");
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/users/verify-password-reset`, {
        email,
        code,
      });
      setMessage("Code verified! Enter your new password.");
      setStep(3);
      setTimerActive(false);
    } catch (err) {
      setMessage("Invalid verification code.");
    }
  };

  const resetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/reset-password`, {
        email,
        newPassword,
        verificationCode: codeDigits.join(""),
      });
      setMessage("Password reset successful!");
      setStep(4);
    } catch (err) {
      setMessage("Error resetting password.");
    }
  };

  const resendCode = () => {
    setTimer(60);
    setTimerActive(true);
    requestCode();
  };

  return (
    <div className="setpw-container">
      <div className="setpw-card">
        <h2 className="setpw-title">Reset Password</h2>
        <p className="setpw-message">{message}</p>

        {/* Step 1 */}
        {step === 1 && (
          <>
            <input
              className="setpw-input"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="setpw-button" onClick={requestCode}>
              Send Verification Code
            </button>
          </>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <>
            <p className="setpw-instruction">Enter the 6-digit code sent to your email</p>
            <div className="code-input-container">
              {codeDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  className="code-input"
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                />
              ))}
            </div>
            <div className="timer-container">
              {timerActive ? (
                <p className="timer-text">Time remaining: {timer}s</p>
              ) : (
                <button className="resend-button" onClick={resendCode}>
                  Resend Code
                </button>
              )}
            </div>
            <button className="setpw-button" onClick={verifyCode}>Verify Code</button>
          </>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <>
            <input
              type="password"
              className="setpw-input"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <input
              type="password"
              className="setpw-input"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button className="setpw-button" onClick={resetPassword}>
              Reset Password
            </button>
          </>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="setpw-success">
            <h3>Password changed successfully!</h3>
            <a className="setpw-login-link" href="/login">Go to Login</a>
          </div>
        )}
      </div>
    </div>
  );
}
